import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [hasPriceAccess, setHasPriceAccess] = useState(false);
  const { customer } = useCustomerAuth();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`/api/store/products/${id}`, { headers });

      if (!response.ok) {
        if (response.status === 404) {
          setError('Producto no encontrado');
        } else {
          setError('Error al cargar el producto');
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('Product data received:', data);
      console.log('Product images:', data.images);
      console.log('Related products:', data.related_products);
      setProduct(data);
      setHasPriceAccess(data.hasPriceAccess || false);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setError('Error al cargar el producto');
      setLoading(false);
    }
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
  };

  const nextImage = () => {
    if (product.images && product.images.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (product.images && product.images.length > 1) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  const renderMarkdown = (markdown) => {
    if (!markdown) return '';

    let html = markdown;

    // Escape HTML characters first
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Bold and italic
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Lists
    html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
    html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
    html = html.replace(/^(\d+)\. (.*$)/gim, '<li>$2</li>');

    // Wrap consecutive list items in ul/ol
    html = html.replace(/(<li>.*<\/li>)/gs, (match) => {
      const lines = match.split('\n');
      let result = '<ul>';
      lines.forEach(line => {
        if (line.trim().startsWith('<li>')) {
          result += line.trim() + '\n';
        }
      });
      result += '</ul>';
      return result;
    });

    // Tables
    const lines = html.split('\n');
    let inTable = false;
    let tableHtml = '';
    let result = '';
    let isFirstRow = true;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line.startsWith('|') && line.endsWith('|')) {
        // Check if this is a separator row (contains only dashes, pipes, and spaces)
        const isSeparatorRow = /^\|[\s\-|]+\|$/.test(line);

        if (isSeparatorRow) {
          // Skip separator rows completely
          continue;
        }

        if (!inTable) {
          inTable = true;
          tableHtml = '<table class="technical-table">\n';
          isFirstRow = true;
        }

        const cells = line.split('|').slice(1, -1).map(cell => cell.trim());

        if (isFirstRow) {
          // First row is always header
          tableHtml += '<tr>';
          cells.forEach(cell => {
            tableHtml += `<th>${cell}</th>`;
          });
          tableHtml += '</tr>\n';
          isFirstRow = false;
        } else {
          // Subsequent rows are data rows
          tableHtml += '<tr>';
          cells.forEach(cell => {
            tableHtml += `<td>${cell}</td>`;
          });
          tableHtml += '</tr>\n';
        }
      } else {
        if (inTable) {
          tableHtml += '</table>';
          result += tableHtml + '\n';
          inTable = false;
          tableHtml = '';
          isFirstRow = true;
        }
        result += lines[i] + '\n';
      }
    }

    if (inTable) {
      tableHtml += '</table>';
      result += tableHtml;
    }

    // Convert line breaks to paragraphs
    result = result.replace(/\n\n/g, '</p><p>');
    result = '<p>' + result + '</p>';

    // Clean up empty paragraphs
    result = result.replace(/<p><\/p>/g, '');
    result = result.replace(/<p>\s*<\/p>/g, '');

    return result;
  };

  if (loading) {
    return (
      <div className="product-detail-loading">
        <div className="loading-spinner"></div>
        <p>Cargando producto...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/tienda')} className="back-btn">
          Volver a la tienda
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-error">
        <h2>Producto no encontrado</h2>
        <button onClick={() => navigate('/tienda')} className="back-btn">
          Volver a la tienda
        </button>
      </div>
    );
  }

  return (
    <div className="product-detail-container">
      <div className="product-detail-content">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/tienda">Tienda</Link>
          <span> / </span>
          <Link to={`/tienda/categoria/${product.category_id}`}>
            {product.category_name}
          </Link>
          <span> / </span>
          <span>{product.short_title}</span>
        </nav>

        {/* Mobile Product Header */}
        <div className="mobile-product-header">
          <h1 className="product-name-large">{product.short_title}</h1>
          <div className="product-price">
            {hasPriceAccess && product.price !== null && (
              <span>{formatPrice(product.price)}</span>
            )}
          </div>
        </div>

        {/* Main Product Row */}
        <div className="row product-info">
          {/* Left Column - Image Gallery */}
          <div className="col-lg-9 col-md-8 col-sm-12 col-xs-12">
            <section className="product-image-gallery slideshow-gallery">
              {/* Main Gallery Display */}
              <div className="gallery-slides-block">
                <div className="gallery-slides">
                  <div className="gallery-slides-scroll">
                    {product.images && product.images.length > 0 ? (
                      product.images.map((image, index) => (
                        <div
                          key={index}
                          role="tabpanel"
                          className="gallery-slide"
                          id={`gallery-slide-${index}`}
                          data-slide={index}
                          style={{
                            display: index === currentImageIndex ? 'block' : 'none'
                          }}
                        >
                          <img
                            src={image.image_url}
                            alt={product.short_title}
                            onError={(e) => {
                              e.target.src = '/assets/images/Distracker.png';
                            }}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="gallery-slide">
                        <img src="/assets/images/Distracker.png" alt="Default" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Thumbnails */}
              <div className="gallery-thumbnails" role="tablist" aria-label="Choose gallery item to display">
                {product.images && product.images.length > 0 ? (
                  product.images.map((image, index) => (
                    <button
                      key={index}
                      aria-controls={`gallery-slide-${index}`}
                      aria-label={`slide ${index + 1}`}
                      aria-selected={index === currentImageIndex}
                      className={`gallery-thumbnail indicator-image ${index === currentImageIndex ? 'selected' : ''}`}
                      data-indicator={index}
                      data-type="image"
                      role="tab"
                      type="button"
                      onClick={() => handleImageClick(index)}
                    >
                      <img
                        src={image.image_url}
                        alt={`${product.short_title} ${index + 1}`}
                        onError={(e) => {
                          e.target.src = '/assets/images/Distracker.png';
                        }}
                      />
                    </button>
                  ))
                ) : (
                  <div className="no-images-message">
                    <p>No hay imágenes disponibles</p>
                  </div>
                )}
              </div>
            </section>

            {/* Mobile Contact Button - Below Image Gallery */}
            <div className="visible-sm visible-xs mobile-contact-section">
              <div className="mobile-button-row">
                <Link to="/soporte" className="btn btn-secondary">
                  Contactar
                </Link>
              </div>
            </div>

            {/* Product Details Sections - Inside Left Column */}
            <div className="prod-info">
              {/* Description */}
              {product.description && (
                <section className="prod-accordion-content prod-accordion-content-description">
                  <div className="tab-heading-row">
                    <h2>Descripción</h2>
                  </div>
                  <div className="panel-collapse collapse in">
                    <div
                      className="markdown-content"
                      dangerouslySetInnerHTML={{
                        __html: renderMarkdown(product.description)
                      }}
                    />
                  </div>
                </section>
              )}

              {/* Technical Details */}
              {product.technical_details && (
                <section className="prod-accordion-content prod-accordion-content-technical-details">
                  <div className="tab-heading-row">
                    <h2>Detalles Técnicos</h2>
                  </div>
                  <div className="panel-collapse collapse in">
                    <div
                      className="markdown-content"
                      dangerouslySetInnerHTML={{
                        __html: renderMarkdown(product.technical_details)
                      }}
                    />
                  </div>
                </section>
              )}

              {/* Datasheet Documentation Section */}
              {(() => {
                // Check for new datasheet_links JSON field first
                if (product.datasheet_links) {
                  try {
                    const datasheetLinks = JSON.parse(product.datasheet_links);
                    if (datasheetLinks && datasheetLinks.length > 0) {
                      return (
                        <section className="prod-accordion-content prod-accordion-content-datasheet">
                          <div className="tab-heading-row">
                            <h2>Documentación</h2>
                          </div>
                          <div className="panel-collapse collapse in">
                            <div className="datasheet-links">
                              {datasheetLinks.map((datasheet, index) => (
                                <div key={datasheet.id || index} className="datasheet-item">
                                  <div className="datasheet-icon">
                                    <i className="fas fa-file-pdf"></i>
                                  </div>
                                  <div className="datasheet-info">
                                    <h4>{datasheet.title || 'Documento Técnico'}</h4>
                                    <a
                                      href={datasheet.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="datasheet-download-btn"
                                    >
                                      <i className="fas fa-download"></i>
                                      Descargar PDF
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </section>
                      );
                    }
                  } catch (error) {
                    console.error('Error parsing datasheet links:', error);
                  }
                }

                return null;
              })()}

              {/* Video Links Section */}
              {product.video_links && (() => {
                try {
                  const videoLinks = JSON.parse(product.video_links);
                  if (videoLinks && videoLinks.length > 0) {
                    return (
                      <section className="prod-accordion-content prod-accordion-content-video-links">
                        <div className="tab-heading-row">
                          <h2>Videos Relacionados</h2>
                        </div>
                        <div className="panel-collapse collapse in">
                          <div className="video-links-grid">
                            {videoLinks.map((video, index) => (
                              <div key={index} className="video-link-item">
                                <a
                                  href={video.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="video-link"
                                >
                                  <div className="video-thumbnail">
                                    <img
                                      src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`}
                                      alt={video.title}
                                      onError={(e) => {
                                        e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                                      }}
                                    />
                                    <div className="play-button">
                                      <i className="fas fa-play"></i>
                                    </div>
                                  </div>
                                  <div className="video-info">
                                    <p>Ver en YouTube</p>
                                  </div>
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      </section>
                    );
                  }
                } catch (error) {
                  console.error('Error parsing video links:', error);
                }
                return null;
              })()}
            </div>
          </div>

          {/* Right Column - Product Info */}
          <div className="col-lg-3 col-md-4 hidden-sm hidden-xs">
            <div className="prod-right-side">
              <h1 className="products_name">{product.short_title}</h1>

              {product.long_title && (
                <div className="product-long-title">
                  {product.long_title}
                </div>
              )}

              <div className="price-info-text">
                Nuestro equipo le ayudará a encontrar la mejor opción y cotización según su proyecto.
              </div>

              <div className="prod-price">
                {hasPriceAccess && product.price !== null && (
                  <span>{formatPrice(product.price)}</span>
                )}
              </div>

              <div className="prod-stock">
                <div className="product-actions">
                  <Link to="/soporte" className="btn btn-secondary">
                    Contactar
                  </Link>
                </div>

                {product.datasheet_url && (
                  <div className="product-datasheet">
                    <a
                      href={product.datasheet_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline"
                    >
                      Descargar Ficha Técnica
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
